import React, { useState, useEffect } from 'react';
import {
  X,
  ChevronLeft,
  ExternalLink,
  Github,
  BookOpen,
  Settings,
  Plus,
  Edit,
  Trash2,
  CheckCircle,
  AlertCircle,
  Clock
} from 'lucide-react';
import { evaluationFrameworksApi } from '../services/api';
import { FrameworkWithMetrics, FrameworkMetric, FrameworkMetricCreate, FrameworkMetricUpdate } from '../types';
import MetricModal from './MetricModal';

export interface FrameworkDetailsViewProps {
  framework: FrameworkWithMetrics;
  onClose: () => void;
  onFrameworkUpdate: () => void;
  addNotification: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
  /** When 'inline', show "Back to Frameworks" instead of X (for tab view) */
  variant?: 'modal' | 'inline';
}

const FrameworkDetailsView: React.FC<FrameworkDetailsViewProps> = ({
  framework,
  onClose,
  onFrameworkUpdate,
  addNotification,
  variant = 'modal'
}) => {
  const [metrics, setMetrics] = useState<FrameworkMetric[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddMetricModal, setShowAddMetricModal] = useState(false);
  const [showEditMetricModal, setShowEditMetricModal] = useState(false);
  const [showDeleteMetricModal, setShowDeleteMetricModal] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState<FrameworkMetric | null>(null);
  const [editingMetric, setEditingMetric] = useState<FrameworkMetric | null>(null);
  const [metricTypes, setMetricTypes] = useState<string[]>([]);
  const [metricStatuses, setMetricStatuses] = useState<string[]>([]);

  useEffect(() => {
    setMetrics(framework.metrics || []);
    loadMetadata();
  }, [framework]);

  const loadMetadata = async () => {
    try {
      const [types, statuses] = await Promise.all([
        evaluationFrameworksApi.getMetricTypes(),
        evaluationFrameworksApi.getMetricStatuses()
      ]);
      setMetricTypes(types);
      setMetricStatuses(statuses);
    } catch (error) {
      console.error('Error loading metadata:', error);
    }
  };

  const handleAddMetric = async (metricData: FrameworkMetricCreate) => {
    try {
      setLoading(true);
      const newMetric = await evaluationFrameworksApi.createFrameworkMetric(framework.id, metricData);
      setMetrics(prev => [...prev, newMetric]);
      setShowAddMetricModal(false);
      addNotification('Metric added successfully', 'success');
      onFrameworkUpdate();
    } catch (error) {
      console.error('Error adding metric:', error);
      addNotification('Failed to add metric', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEditMetric = async (metricData: FrameworkMetricUpdate) => {
    if (!editingMetric) return;
    try {
      setLoading(true);
      const updatedMetric = await evaluationFrameworksApi.updateFrameworkMetric(editingMetric.id, metricData);
      setMetrics(prev => prev.map(m => m.id === editingMetric.id ? updatedMetric : m));
      setShowEditMetricModal(false);
      setEditingMetric(null);
      addNotification('Metric updated successfully', 'success');
      onFrameworkUpdate();
    } catch (error) {
      console.error('Error updating metric:', error);
      addNotification('Failed to update metric', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMetric = async () => {
    if (!selectedMetric) return;
    try {
      setLoading(true);
      await evaluationFrameworksApi.deleteFrameworkMetric(selectedMetric.id);
      setMetrics(prev => prev.filter(m => m.id !== selectedMetric.id));
      setShowDeleteMetricModal(false);
      setSelectedMetric(null);
      addNotification('Metric deleted successfully', 'success');
      onFrameworkUpdate();
    } catch (error) {
      console.error('Error deleting metric:', error);
      addNotification('Failed to delete metric', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-200 bg-green-900/50';
      case 'deprecated': return 'text-red-200 bg-red-900/50';
      case 'experimental': return 'text-amber-200 bg-amber-900/50';
      default: return 'text-slate-300 bg-slate-600';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircle className="h-4 w-4" />;
      case 'deprecated': return <AlertCircle className="h-4 w-4" />;
      case 'experimental': return <Clock className="h-4 w-4" />;
      default: return <AlertCircle className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-100">{framework.name}</h2>
          <p className="text-slate-400 mt-1">{framework.description}</p>
        </div>
        <button
          onClick={onClose}
          className="inline-flex items-center text-slate-400 hover:text-slate-200"
        >
          {variant === 'inline' ? (
            <>
              <ChevronLeft className="h-5 w-5 mr-1" />
              Back to Frameworks
            </>
          ) : (
            <X className="h-6 w-6" />
          )}
        </button>
      </div>

      {/* Framework Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
          <h3 className="text-lg font-medium text-slate-100 mb-3">Framework Details</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-400">Type:</span>
              <span className="font-medium text-slate-200">{framework.framework_type}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Status:</span>
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(framework.status)}`}>
                {getStatusIcon(framework.status)}
                <span className="ml-1">{framework.status}</span>
              </span>
            </div>
            {framework.version && (
              <div className="flex justify-between">
                <span className="text-slate-400">Version:</span>
                <span className="font-medium text-slate-200">{framework.version}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-slate-400">Score Range:</span>
              <span className="font-medium text-slate-200">{framework.score_range_min} - {framework.score_range_max}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Max Concurrent:</span>
              <span className="font-medium text-slate-200">{framework.max_concurrent_evaluations}</span>
            </div>
          </div>
        </div>

        <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
          <h3 className="text-lg font-medium text-slate-100 mb-3">Links & Resources</h3>
          <div className="space-y-2">
            {framework.documentation_url && (
              <a
                href={framework.documentation_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-sm text-purple-300 hover:text-purple-200"
              >
                <BookOpen className="h-4 w-4 mr-2" />
                Documentation
                <ExternalLink className="h-3 w-3 ml-1" />
              </a>
            )}
            {framework.github_url && (
              <a
                href={framework.github_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-sm text-purple-300 hover:text-purple-200"
              >
                <Github className="h-4 w-4 mr-2" />
                GitHub Repository
                <ExternalLink className="h-3 w-3 ml-1" />
              </a>
            )}
            {framework.api_endpoint && (
              <div className="flex items-center text-sm text-slate-400">
                <Settings className="h-4 w-4 mr-2" />
                API: {framework.api_endpoint}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Metrics Section */}
      <div className="border-t border-slate-700 pt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-slate-100">Metrics ({metrics.length})</h3>
          <button
            onClick={() => setShowAddMetricModal(true)}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Metric
          </button>
        </div>

        {metrics.length === 0 ? (
          <div className="text-center py-8">
            <Settings className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-slate-100 mb-2">No metrics configured</h4>
            <p className="text-slate-400 mb-4">Add metrics to define how this framework evaluates responses.</p>
            <button
              onClick={() => setShowAddMetricModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add First Metric
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-700">
              <thead className="bg-slate-700/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Required</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Weight</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Priority</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Score Range</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-slate-800 divide-y divide-slate-700">
                {metrics.map((metric) => (
                  <tr key={metric.id} className="hover:bg-slate-700/30">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-slate-100">{metric.name}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400">
                      {metric.metric_type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(metric.status)}`}>
                        {getStatusIcon(metric.status)}
                        <span className="ml-1">{metric.status}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {metric.is_required ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-red-200 bg-red-900/50">
                          Yes
                        </span>
                      ) : (
                        <span className="text-sm text-slate-400">No</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400">
                      {metric.default_weight}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400">
                      {metric.priority}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400">
                      {metric.score_range_min} – {metric.score_range_max}
                    </td>
                    <td className="px-6 py-4 max-w-xs">
                      <span className="text-sm text-slate-400 line-clamp-2" title={metric.description || ''}>
                        {metric.description || '—'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => {
                            setEditingMetric(metric);
                            setShowEditMetricModal(true);
                          }}
                          className="inline-flex items-center px-2 py-1 border border-slate-600 text-xs font-medium rounded text-slate-200 bg-slate-800 hover:bg-slate-700"
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          Edit
                        </button>
                        <button
                          onClick={() => {
                            setSelectedMetric(metric);
                            setShowDeleteMetricModal(true);
                          }}
                          className="inline-flex items-center px-2 py-1 border border-red-800 text-xs font-medium rounded text-red-200 bg-red-900/30 hover:bg-red-800/50"
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <MetricModal
        isOpen={showAddMetricModal}
        onClose={() => setShowAddMetricModal(false)}
        onSuccess={() => {
          evaluationFrameworksApi.getFrameworkWithMetrics(framework.id).then(details => {
            setMetrics(details.metrics || []);
            onFrameworkUpdate();
          });
        }}
        frameworkId={framework.id}
        addNotification={addNotification}
      />

      <MetricModal
        isOpen={showEditMetricModal}
        onClose={() => {
          setShowEditMetricModal(false);
          setEditingMetric(null);
        }}
        onSuccess={() => {
          evaluationFrameworksApi.getFrameworkWithMetrics(framework.id).then(details => {
            setMetrics(details.metrics || []);
            onFrameworkUpdate();
          });
        }}
        frameworkId={framework.id}
        editingMetric={editingMetric}
        addNotification={addNotification}
      />

      {showDeleteMetricModal && selectedMetric && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-slate-800 border-slate-700">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-900/50">
                <AlertCircle className="h-6 w-6 text-red-400" />
              </div>
              <h3 className="text-lg font-medium text-slate-100 mt-4">Delete Metric</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-slate-400">
                  Are you sure you want to delete "{selectedMetric.name}"? This action cannot be undone.
                </p>
              </div>
              <div className="flex items-center justify-center space-x-3 mt-4">
                <button
                  onClick={() => {
                    setShowDeleteMetricModal(false);
                    setSelectedMetric(null);
                  }}
                  className="px-4 py-2 border border-slate-600 text-sm font-medium rounded-md text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteMetric}
                  className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FrameworkDetailsView;
